if (!customElements.get('wlsn-header-search')) {
  class WLSNHeaderSearch extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        lockScroll: 'wlsn-lock-scroll',
        headerSearchToggle: 'js-wlsn-header-search-toggle',
        headerSearchCloseBtn: 'wlsn-search-form__input-clear-btn',
        headerSearch: 'wlsn-header-search',
        headerSearchOverlay: 'js-wlsn-header-search-overlay',
      };

      this.isHeaderSearchOpen = false;

      this.bindMethods = {
        toggleHeaderSearch: this._toggleHeaderSearch.bind(this),
        closeHeaderSearch: this._closeHeaderSearch.bind(this),
        onHeaderSearchOverlayClick: this._onHeaderSearchOverlayClick.bind(this),
        onEscKey: this._onEscKey.bind(this),
      };
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        headerSearchToggles: document.querySelectorAll(`.${this.classes.headerSearchToggle}`),
        headerSearchCloseBtn: this.querySelector(`.${this.classes.headerSearchCloseBtn}`),
        headerSearch: this,
        headerSearchOverlay: this.querySelector(`.${this.classes.headerSearchOverlay}`),
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      const { headerSearchToggles, headerSearchCloseBtn, headerSearchOverlay } = this.elements;
      // Header search
      if (headerSearchToggles.length > 0) {
        headerSearchToggles.forEach((toggle) => {
          toggle.addEventListener('click', this.bindMethods.toggleHeaderSearch);
        });
      }

      if (headerSearchCloseBtn) {
        headerSearchCloseBtn.addEventListener('click', this.bindMethods.closeHeaderSearch);
      }

      if (headerSearchOverlay) {
        headerSearchOverlay.addEventListener('click', this.bindMethods.onHeaderSearchOverlayClick);
      }

      // ESC key handle for all modals
      document.addEventListener('keydown', this.bindMethods.onEscKey);
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      const { headerSearchToggles, headerSearchCloseBtn, headerSearchOverlay } = this.elements;
      if (headerSearchToggles.length > 0) {
        headerSearchToggles.forEach((toggle) => {
          toggle.removeEventListener('click', this.bindMethods.toggleHeaderSearch);
        });
      }

      if (headerSearchCloseBtn) {
        headerSearchCloseBtn.removeEventListener('click', this.bindMethods.closeHeaderSearch);
      }

      if (headerSearchOverlay) {
        headerSearchOverlay.removeEventListener('click', this.bindMethods.onHeaderSearchOverlayClick);
      }

      document.removeEventListener('keydown', this.bindMethods.onEscKey);
    }

    /**
     * Toggle header search open/closed
     * @param {HTMLElement} trigger - The trigger element
     * @returns {void}
     */
    _toggleHeaderSearch(trigger) {
      const { headerSearch } = this.elements;
      if (!trigger) return;
      this._lockScroll();
      if (this.isHeaderSearchOpen) {
        this._closeHeaderSearch();
      } else {
        this.isHeaderSearchOpen = true;
        headerSearch?.classList.add(this.classes.active);
        this._updateOverlayState();
      }
    }

    /**
     * Close header search
     * @returns {void}
     */
    _closeHeaderSearch() {
      const { headerSearch } = this.elements;
      if (!this.isHeaderSearchOpen) return;
      this.isHeaderSearchOpen = false;
      headerSearch?.classList.remove(this.classes.active);
      this._updateOverlayState();
      this._unlockScroll();
    }

    /**
     * Handle header search toggle click event
     * @param {MouseEvent} e - The mouse event
     * @returns {void}
     */
    _onHeaderSearchOverlayClick(e) {
      if (this.isHeaderSearchOpen) {
        this._closeHeaderSearch();
      }
    }

    /**
     * Update overlay state based on header search state
     * @returns {void}
     */
    _updateOverlayState() {
      const { headerSearchOverlay } = this.elements;
      headerSearchOverlay?.classList.toggle(this.classes.active, this.isHeaderSearchOpen);
    }

    /**
     * Handle ESC key press
     * @param {KeyboardEvent} e - The keyboard event
     * @returns {void}
     */
    _onEscKey(e) {
      if (e.key === 'Escape') {
        if (this.isHeaderSearchOpen) {
          this._closeHeaderSearch();
          return;
        }
      }
    }

    /**
     * Lock body scroll
     * @returns {void}
     */
    _lockScroll() {
      document.body.classList.add(this.classes.lockScroll);
    }

    /**
     * Unlock body scroll
     * @returns {void}
     */
    _unlockScroll() {
      document.body.classList.remove(this.classes.lockScroll);
    }
  }

  customElements.define('wlsn-header-search', WLSNHeaderSearch);
}
